import React, { useEffect, useState, useCallback } from "react";
import {
    Modal, Form, Button, Divider, Upload, message, Select, Radio, InputNumber,
} from "antd";
import {
    PictureOutlined, SaveOutlined, UploadOutlined, LoadingOutlined,
    CloseOutlined, VideoCameraOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import AddImage from "../../Global Functions/addImage";
import "./addAndEditHomeBanner.css";

import { getProjectList } from "../../Redux/Actions/projectActions";
import { getEnums } from "../../Redux/Actions/enumsActions";
import { getEventList } from "../../Redux/Actions/eventActions";
import { getNewsList } from "../../Redux/Actions/newsAction";
import { viewBuilderList } from "../../Redux/Actions/builderActions";
import { listRewardVouchers } from "../../Redux/Actions/rewardsAction";
import { viewCollectionsList } from "../../Redux/Actions/collectionsActions";

const { Option } = Select;

const BANNER_TYPES = [
    { value: "project", label: "Project" },
    { value: "event", label: "Event" },
    { value: "reward", label: "Reward" },
    { value: "category", label: "Category" },
    { value: "collection", label: "Collection" },
    { value: "vr", label: "VR" },
    { value: "book_home_vr", label: "Book Home VR" },
    { value: "site_visit", label: "Site Visit" },
    { value: "meeting", label: "Meeting" },
    { value: "loan", label: "Loan" },
    { value: "rental", label: "Rental" },
];

const HOME_TYPE_OPTIONS = [
    { value: "fresh_property", label: "Fresh Property" },
    { value: "resale", label: "Resale" },
    { value: "business", label: "Business" },
    { value: "rental", label: "Rental" },
];

const THEME_OPTIONS = [
    { value: "light", label: "Light" },
    { value: "dark", label: "Dark" },
];

const toOptions = (arr = [], labelKey, valueKey = "_id") =>
    arr.map((item) => ({ label: item[labelKey] ?? item.name ?? "—", value: item[valueKey] }));

const buildTypeData = (type, record) => {
    if (!record) return {};
    switch (type) {
        case "project":
            return {
                images: record.images?.map((img) => img?.url).filter(Boolean) || [],
                cover_photo: record.cover_photo,
                cover_photo_thumbnail: record.cover_photo_thumbnail,
                _id: record._id,
                project_id: record.project_id,
                project_name: record.project_name,
                brand_name: record.brand_name,
                construction_status: record.construction_status,
            };
        case "event":
            return {
                images: record.cover_photo ? [record.cover_photo] : [],
                cover_photo: record.cover_photo,
                _id: record._id,
                event_id: record.event_id,
                event_name: record.event_name,
                event_name_withoutspace: record.event_name_withoutspace,
                organizer_id: record.organizer_id,
                is_free: record.is_free,
                is_remind_before: record.is_remind_before,
                start_date: record.start_date,
                start_time: record.start_time,
                start_date_utc: record.start_date_utc,
                end_date: record.end_date,
                end_date_utc: record.end_date_utc,
                end_time: record.end_time,
                no_of_audience: record.no_of_audience,
                about_event: record.about_event,
                status: record.status,
                location: record.location,
            };
        case "reward":
            return {
                images: record.image ? [record.image] : [],
                cover_photo: record.image,
                voucher: record.voucher,
                title: record.title,
                description: record.description,
                reward_points: record.reward_points,
                brochure: record.brochure,
                position: record.position,
                primary: record.primary,
                terms_and_conditions: record.terms_and_conditions,
                reward_for: record.reward_for,
                how_to_redeem: record.how_to_redeem,
                category: record.category,
                status: record.status,
                country: record.country,
            };
        case "builder":
            return {
                images: record.brand_logo ? [record.brand_logo] : [],
                cover_photo: record.brand_logo || record.profile_image,
                builder_id: record.builder_id,
                brand_name: record.brand_name,
                organization_name: record.organization_name,
                primary_contact_number: record.primary_contact_number,
                primary_country_code: record.primary_country_code,
                primary_email: record.primary_email,
                rera_id: record.rera_id,
                profile_image: record.profile_image,
                role: record.role,
                account_status: record.account_status,
                total_projects: record.total_projects,
                live_projects: record.live_projects,
            };
        default:
            return { ...record };
    }
};

const HomeBannerEditModal = ({ visible, onClose, bannerData, onSave }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const [selectedType, setSelectedType] = useState(null);
    const [selectedTypeRecord, setSelectedTypeRecord] = useState(null);
    const [mediaMode, setMediaMode] = useState("image");
    const [fileList, setFileList] = useState([]);
    const [videoFileList, setVideoFileList] = useState([]);
    const [thumbnailFileList, setThumbnailFileList] = useState([]);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
    const [enumsProjects, setEnumsProjects] = useState([]);

    const { projects, loading: projectLoading } = useSelector((s) => s.projects || {});
    const { events, loading: eventLoading } = useSelector((s) => s.events || {});
    const { builders, loading: builderLoading } = useSelector((s) => s.builders || {});
    const { news, loading: newsLoading } = useSelector((s) => s.news || {});
    const { vouchers = {}, loading: rewardLoading } = useSelector((s) => s.rewards || {});
    const { enumsData } = useSelector((s) => s.enumsData || {});

    const projectList = projects?.data || [];
    const eventList = events?.data || [];
    const builderList = builders?.data || [];
    const newsList = news?.data || [];
    const rewardList = Array.isArray(vouchers?.data) ? vouchers.data : [];

    const categoryData = useSelector((s) => s.categories?.categories?.data || s.categories?.categories || []);
    const collections = useSelector((state) => state.collections);
    const collectionData = useSelector((s) => s.collections?.collections?.data || s.collections?.collections || []);
    const vrData = useSelector((s) => s.vrVisit?.vrVisits?.data || s.vrVisit?.vrVisits || []);
    const bookHVData = useSelector((s) => s.homeVisit?.appointments?.data || s.homeVisit?.appointments || []);
    const siteVisitData = useSelector((s) => s.siteVisit?.siteVisits?.data || s.siteVisit?.siteVisits || []);
    const meetingData = useSelector((s) => s.meetings?.meetings?.data || s.meetings?.meetings || []);
    const loanData = useSelector((s) => s.loans?.loans?.data || s.loans?.loans || []);

    useEffect(() => {
        if (!visible) return;
        if (!enumsData?.data) dispatch(getEnums());
        if (!eventList.length) dispatch(getEventList({ page: 1, limit: 1000, is_deleted: false }));
        if (!newsList.length) dispatch(getNewsList({ page: 1, limit: 1000, is_deleted: false }));
        if (!rewardList.length) dispatch(listRewardVouchers({ page: 1, limit: 1000, is_deleted: false }));
        if (!builderList.length) dispatch(viewBuilderList({ page: 1, limit: 1000, is_deleted: false }));
        if (!collections.length) dispatch(viewCollectionsList({ page: 1, limit: 1000, is_deleted: false }));
    }, [visible]);

    useEffect(() => {
        if (enumsData?.data?.ProjectStatus) {
            const statuses = Object.values(enumsData.data.ProjectStatus).filter((s) => s !== "declined");
            if (statuses.length) setEnumsProjects(statuses);
        }
    }, [enumsData]);

    const fetchProjects = useCallback(() => {
        if (!enumsProjects.length) return;
        dispatch(getProjectList({ page: 1, limit: 1150, status: enumsProjects, is_deleted: false }));
    }, [dispatch, enumsProjects]);

    useEffect(() => { fetchProjects(); }, [fetchProjects]);

    const TYPE_CONFIG = {
        project: { list: projectList, labelKey: "project_name" },
        event: { list: eventList, labelKey: "event_name" },
        reward: { list: rewardList, labelKey: "title" },
        builder: { list: builderList, labelKey: "brand_name" },
        category: { list: categoryData, labelKey: "name" },
        collection: { list: collectionData, labelKey: "name" },
        vr: { list: vrData, labelKey: "name" },
        book_home_vr: { list: bookHVData, labelKey: "name" },
        site_visit: { list: siteVisitData, labelKey: "name" },
        meeting: { list: meetingData, labelKey: "name" },
        loan: { list: loanData, labelKey: "loan_type" },
    };

    const activeConfig = selectedType ? TYPE_CONFIG[selectedType] : null;
    const activeOptions = activeConfig ? toOptions(activeConfig.list, activeConfig.labelKey) : [];
    const activeLabel = BANNER_TYPES.find((t) => t.value === selectedType)?.label ?? selectedType;

    useEffect(() => {
        if (visible && bannerData) {
            const imgList = bannerData.home_image
                ? [{ uid: "-1", name: "current-banner.jpg", status: "done", url: bannerData.home_image }]
                : [];
            const vidList = bannerData.home_videos
                ? [{ uid: "-2", name: "current-video.mp4", status: "done", url: bannerData.home_videos }]
                : [];
            const thumbList = bannerData.thumbnail
                ? [{ uid: "-3", name: "current-thumbnail.jpg", status: "done", url: bannerData.thumbnail }]
                : [];

            setMediaMode(bannerData.home_videos ? "video" : "image");
            setFileList(imgList);
            setVideoFileList(vidList);
            setThumbnailFileList(thumbList);
            setSelectedType(bannerData.type || null);
            setSelectedTypeRecord(
                bannerData.type_data && Object.keys(bannerData.type_data).length > 0
                    ? bannerData.type_data : null
            );
            form.setFieldsValue({
                home_type: bannerData.home_type || null,
                type: bannerData.type,
                type_record_id: bannerData.type_data?._id,
                theme: bannerData.theme || null,
                position: bannerData.position ?? null,
                home_image: imgList,
                home_videos: vidList,
                thumbnail: thumbList,
            });
        } else if (visible) {
            form.resetFields();
            setFileList([]);
            setVideoFileList([]);
            setThumbnailFileList([]);
            setSelectedType(null);
            setSelectedTypeRecord(null);
            setMediaMode("image");
        }
    }, [visible, bannerData, form]);

    const handleTypeChange = (val) => {
        setSelectedType(val);
        setSelectedTypeRecord(null);
        form.setFieldsValue({ type_record_id: undefined });
    };

    const handleRecordSelect = (id) => {
        const config = TYPE_CONFIG[selectedType];
        if (!config) return;
        const record = config.list.find((item) => item._id === id) || null;
        setSelectedTypeRecord(record);
    };

    const handleMediaModeChange = (e) => {
        setMediaMode(e.target.value);
        setFileList([]);
        setVideoFileList([]);
        form.setFieldsValue({ home_image: [], home_videos: [] });
    };

    const customImageUpload = async ({ file, onSuccess, onError }) => {
        try {
            setUploadingImage(true);
            const sanitizedFile = new File([file], file.name.replace(/\s+/g, "_"), { type: file.type });
            const url = await AddImage(sanitizedFile);
            onSuccess({ url }, file);
            setFileList((prev) => {
                const updated = prev.map((i) => i.uid === file.uid ? { ...i, status: "done", url } : i);
                form.setFieldsValue({ home_image: updated });
                return updated;
            });
        } catch (err) {
            message.error("Image upload failed!");
            onError(err);
        } finally {
            setUploadingImage(false);
        }
    };

    const customVideoUpload = async ({ file, onSuccess, onError }) => {
        try {
            setUploadingVideo(true);
            const sanitizedFile = new File([file], file.name.replace(/\s+/g, "_"), { type: file.type });
            const url = await AddImage(sanitizedFile);
            onSuccess({ url }, file);
            setVideoFileList((prev) => {
                const updated = prev.map((i) => i.uid === file.uid ? { ...i, status: "done", url } : i);
                form.setFieldsValue({ home_videos: updated });
                return updated;
            });
        } catch (err) {
            message.error("Video upload failed!");
            onError(err);
        } finally {
            setUploadingVideo(false);
        }
    };

    const customThumbnailUpload = async ({ file, onSuccess, onError }) => {
        try {
            setUploadingThumbnail(true);
            const sanitizedFile = new File([file], file.name.replace(/\s+/g, "_"), { type: file.type });
            const url = await AddImage(sanitizedFile);
            onSuccess({ url }, file);
            setThumbnailFileList((prev) => {
                const updated = prev.map((i) => i.uid === file.uid ? { ...i, status: "done", url } : i);
                form.setFieldsValue({ thumbnail: updated });
                return updated;
            });
        } catch (err) {
            message.error("Thumbnail upload failed!");
            onError(err);
        } finally {
            setUploadingThumbnail(false);
        }
    };

    const handleFinish = async (values) => {
        const payload = {
            home_type: values.home_type || "",
            type: values.type,
            type_data: buildTypeData(values.type, selectedTypeRecord),
            theme: values.theme,
            position: values.position ?? null,
            home_image: mediaMode === "image" ? (values.home_image?.[0]?.url || "") : "",
            home_videos: mediaMode === "video" ? (values.home_videos?.[0]?.url || "") : "",
            thumbnail: values.thumbnail?.[0]?.url || "",
        };
        await onSave(payload);
    };

    const imageUploadProps = {
        customRequest: customImageUpload,
        maxCount: 1, listType: "picture", accept: "image/*",
        showUploadList: true, fileList,
        onChange: ({ fileList: fl }) => setFileList(fl),
    };

    const videoUploadProps = {
        customRequest: customVideoUpload,
        maxCount: 1, listType: "picture", accept: "video/*",
        showUploadList: true, fileList: videoFileList,
        onChange: ({ fileList: fl }) => setVideoFileList(fl),
    };

    const thumbnailUploadProps = {
        customRequest: customThumbnailUpload,
        maxCount: 1, listType: "picture", accept: "image/*",
        showUploadList: true, fileList: thumbnailFileList,
        onChange: ({ fileList: fl }) => setThumbnailFileList(fl),
    };

    return (
        <Modal
            title={
                <div className="hb-modal-header-content">
                    <PictureOutlined className="hb-modal-header-icon" style={{ color: "white" }} />
                    <span style={{ color: "white" }}>
                        {bannerData?._id ? "EDIT BANNER" : "ADD BANNER"}
                    </span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            closeIcon={
                <Button
                    type="text"
                    icon={<CloseOutlined style={{ color: "white", fontSize: 18 }} />}
                    onClick={onClose}
                    style={{
                        marginTop: 1, marginRight: 47, padding: 6,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: "1px solid white", color: "white",
                    }}
                    className="hb-modal-close-btn"
                />
            }
            destroyOnClose
            width={700}
            className="hb-modal-override"
            styles={{
                header: {
                    background: "#070C18",
                    borderRadius: "12px 12px 0 0",
                    padding: "16px 24px",
                    borderBottom: "none",
                },
                body: { padding: "20px 24px", background: "#f5f6fa" },
            }}
        >
            <Form form={form} layout="vertical" onFinish={handleFinish} className="hb-modal-form">

                {/* ── Row 1: Home Type + Position ── */}
                <div className="hb-form-section">
                    <h3 className="hb-form-section-header">General Settings</h3>
                    <div className="hb-form-grid">
                        <Form.Item
                            label="Home Type"
                            name="home_type"
                            rules={[{ required: true, message: "Please select a home type" }]}
                            className="hb-form-item"
                        >
                            <Select size="large" placeholder="Select home type" className="hb-form-input" allowClear>
                                {HOME_TYPE_OPTIONS.map((t) => (
                                    <Option key={t.value} value={t.value}>{t.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Position"
                            name="position"
                            rules={[{ required: true, message: "Please enter a position" }]}
                            className="hb-form-item"
                        >
                            <InputNumber
                                size="large"
                                min={1}
                                placeholder="e.g. 1"
                                className="hb-form-input hb-input-number"
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </div>
                </div>

                <Divider className="hb-form-divider" />

                {/* ── Row 2: Banner Type + Theme ── */}
                <div className="hb-form-section">
                    <h3 className="hb-form-section-header">Banner Configuration</h3>
                    <div className="hb-form-grid">
                        <Form.Item
                            label="Banner Type"
                            name="type"
                            // rules={[{ message: "Please select banner type" }]}
                            className="hb-form-item"
                        >
                            <Select
                                size="large"
                                placeholder="Select banner type"
                                className="hb-form-input"
                                onChange={handleTypeChange}
                                allowClear
                                onClear={() => {
                                    setSelectedType(null);
                                    setSelectedTypeRecord(null);
                                    form.setFieldsValue({ type: undefined, type_record_id: undefined });
                                }}
                            >
                                {BANNER_TYPES.map((t) => (
                                    <Option key={t.value} value={t.value}>{t.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Theme"
                            name="theme"
                            rules={[{ required: true, message: "Please select a theme" }]}
                            className="hb-form-item"
                        >
                            <Select size="large" placeholder="Select theme" className="hb-form-input">
                                {THEME_OPTIONS.map((t) => (
                                    <Option key={t.value} value={t.value}>{t.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    {selectedType && (
                        <Form.Item
                            label={`Select ${activeLabel}`}
                            name="type_record_id"
                            className="hb-form-item"
                            style={{ marginBottom: 0 }}
                        >
                            <Select
                                size="large"
                                placeholder={`Choose a ${activeLabel}…`}
                                className="hb-form-input"
                                showSearch
                                allowClear
                                optionFilterProp="children"
                                onChange={handleRecordSelect}
                                loading={
                                    (selectedType === "project" && projectLoading) ||
                                    (selectedType === "event" && eventLoading) ||
                                    (selectedType === "builder" && builderLoading) ||
                                    (selectedType === "reward" && rewardLoading) ||
                                    (selectedType === "news" && newsLoading)
                                }
                                notFoundContent={`No ${activeLabel} data available`}
                            >
                                {activeOptions.map((opt) => (
                                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                </div>

                <Divider className="hb-form-divider" />

                {/* ── Banner Media ── */}
                <div className="hb-form-section">
                    <h3 className="hb-form-section-header">Banner Media</h3>

                    <div className="hb-media-toggle">
                        <Radio.Group value={mediaMode} onChange={handleMediaModeChange}>
                            <Radio.Button value="image"><UploadOutlined /> Image</Radio.Button>
                            <Radio.Button value="video"><VideoCameraOutlined /> Video</Radio.Button>
                        </Radio.Group>
                        <span className="hb-media-toggle-hint">Upload either an image or a video — not both.</span>
                    </div>

                    <div className="hb-form-grid" style={{ marginTop: 14 }}>
                        {mediaMode === "image" ? (
                            <Form.Item
                                label="Upload Image"
                                name="home_image"
                                valuePropName="fileList"
                                getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList || []}
                                className="hb-form-item"
                                style={{ marginBottom: 0 }}
                            >
                                <Upload {...imageUploadProps}>
                                    <Button className="hb-upload-btn">
                                        {uploadingImage ? <LoadingOutlined /> : <UploadOutlined />}
                                        {uploadingImage ? "Uploading…" : "Upload Image"}
                                    </Button>
                                </Upload>
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label="Upload Video"
                                name="home_videos"
                                valuePropName="fileList"
                                getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList || []}
                                className="hb-form-item"
                                style={{ marginBottom: 0 }}
                            >
                                <Upload {...videoUploadProps}>
                                    <Button className="hb-upload-btn hb-upload-btn--video">
                                        {uploadingVideo ? <LoadingOutlined /> : <VideoCameraOutlined />}
                                        {uploadingVideo ? "Uploading…" : "Upload Video"}
                                    </Button>
                                </Upload>
                            </Form.Item>
                        )}

                        <Form.Item
                            label="Thumbnail"
                            name="thumbnail"
                            valuePropName="fileList"
                            getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList || []}
                            className="hb-form-item"
                            style={{ marginBottom: 0 }}
                        >
                            <Upload {...thumbnailUploadProps}>
                                <Button className="hb-upload-btn">
                                    {uploadingThumbnail ? <LoadingOutlined /> : <PictureOutlined />}
                                    {uploadingThumbnail ? "Uploading…" : "Upload Thumbnail"}
                                </Button>
                            </Upload>
                        </Form.Item>
                    </div>
                </div>

                <Divider className="hb-form-divider" />

                <Form.Item style={{ marginBottom: 0 }}>
                    <div className="hb-form-actions">
                        <Button size="large" onClick={onClose} className="hb-btn hb-btn-cancel">
                            Cancel
                        </Button>
                        <Button
                            htmlType="submit"
                            size="large"
                            icon={<SaveOutlined />}
                            loading={uploadingImage || uploadingVideo || uploadingThumbnail}
                            className="hb-btn hb-btn-primary"
                        >
                            <span style={{ color: "white" }}>
                                {bannerData?._id ? "Update Banner" : "Add Banner"}
                            </span>
                        </Button>
                    </div>
                </Form.Item>

            </Form>
        </Modal>
    );
};

export default HomeBannerEditModal;